import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { api } from '@/lib/api';
import { formatPrice } from '@/components/PropertyCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { MapPin, Phone, MessageCircle, Building2, ShieldCheck, Check, Calendar, IndianRupee } from 'lucide-react';

const FALLBACK_IMG = 'https://images.unsplash.com/photo-1649989184479-73ab3b750a73?crop=entropy&cs=srgb&fm=jpg&q=85';

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [broker, setBroker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState({ name: '', mobile: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    load();
  }, [id]);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
      // Get broker profile
      if (data.broker_id) {
        try {
          const brokers = await api.get('/public/projects');
          // Get broker via a helper endpoint - for now build from broker_id lookup
        } catch (e) {}
      }
    } catch (e) {
      toast.error('Project not found');
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!project) return;
    setSubmitting(true);
    try {
      await api.post('/leads', {
        ...lead,
        broker_id: project.broker_id,
        project_id: project.id,
        source: 'website'
      });
      toast.success('Enquiry submitted! Our broker will contact you soon.');
      setLead({ name: '', mobile: '', email: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!project) return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-bold">Project not found</h1>
        <Link to="/search"><Button className="mt-4">Browse Projects</Button></Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* HERO */}
      <div className="relative h-96 bg-slate-900">
        <img src={project.cover_image_url || FALLBACK_IMG} alt={project.name} className="absolute inset-0 w-full h-full object-cover opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-8">
          <div className="flex items-center gap-2 mb-3">
            {project.rera_number && (
              <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white gap-1"><ShieldCheck size={12}/> RERA {project.rera_number}</Badge>
            )}
            <Badge className="bg-[#F97316] hover:bg-[#F97316]">{project.project_status?.replace('_', ' ')}</Badge>
          </div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white">{project.name}</h1>
          {project.builder_name && <p className="text-slate-200 mt-2">by <span className="font-semibold">{project.builder_name}</span></p>}
          <div className="flex items-center gap-1 text-slate-200 mt-2">
            <MapPin size={16} /> <span>{project.locality}, {project.city}, {project.state}</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT */}
          <div className="lg:col-span-2 space-y-6">
            {/* Price & Config */}
            <div className="bg-white rounded-lg border border-slate-200 p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Price Range</div>
                <div className="font-display font-extrabold text-lg text-[#0F2C59] mt-1">{formatPrice(project.price_min)} - {formatPrice(project.price_max)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Configurations</div>
                <div className="font-semibold text-slate-900 mt-1">{project.configurations?.join(', ') || 'N/A'}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Unit Sizes</div>
                <div className="font-semibold text-slate-900 mt-1">{project.unit_sizes || 'N/A'}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Possession</div>
                <div className="font-semibold text-slate-900 mt-1">{project.possession_date || 'TBA'}</div>
              </div>
            </div>

            {/* Overview */}
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h2 className="font-display font-bold text-xl text-[#0F2C59]">Project Overview</h2>
              <p className="text-slate-600 mt-3 leading-relaxed whitespace-pre-line">{project.overview}</p>
            </div>

            {/* Highlights */}
            {project.highlights?.length > 0 && (
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h2 className="font-display font-bold text-xl text-[#0F2C59]">Project Highlights</h2>
                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {project.highlights.map((h, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-700"><Check size={16} className="text-emerald-600" /> {h}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Amenities */}
            {project.amenities?.length > 0 && (
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h2 className="font-display font-bold text-xl text-[#0F2C59]">Amenities</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {project.amenities.map((a, i) => (
                    <span key={i} className="px-3 py-1 bg-slate-100 rounded-md text-sm text-slate-700">{a}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Location advantages */}
            {project.location_advantages?.length > 0 && (
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h2 className="font-display font-bold text-xl text-[#0F2C59]">Location Advantages</h2>
                <ul className="mt-3 space-y-2">
                  {project.location_advantages.map((l, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-700"><MapPin size={14} className="text-[#F97316]" /> {l}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Gallery */}
            {project.gallery_images?.length > 0 && (
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h2 className="font-display font-bold text-xl text-[#0F2C59] mb-4">Gallery</h2>
                <div className="grid grid-cols-2 gap-3">
                  {project.gallery_images.map((img, i) => (
                    <img key={i} src={img} alt="" className="w-full h-48 object-cover rounded-md" />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT - Enquiry Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-slate-200 p-6 sticky top-20">
              <h3 className="font-display font-bold text-xl text-[#0F2C59]">Enquire Now</h3>
              <p className="text-xs text-slate-500 mt-1">Get instant callback from our verified broker</p>

              <form onSubmit={submitLead} className="mt-4 space-y-3">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Name *</Label>
                  <Input value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} required data-testid="enquiry-name-input" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Mobile *</Label>
                  <Input type="tel" value={lead.mobile} onChange={(e) => setLead({ ...lead, mobile: e.target.value })} required data-testid="enquiry-mobile-input" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</Label>
                  <Input type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} data-testid="enquiry-email-input" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Message</Label>
                  <Textarea value={lead.message} onChange={(e) => setLead({ ...lead, message: e.target.value })} rows={3} placeholder="I'm interested in..." data-testid="enquiry-message-input" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-[#F97316] hover:bg-[#EA580C] font-semibold" data-testid="enquiry-submit-btn">
                  {submitting ? 'Submitting...' : 'Send Enquiry'}
                </Button>
              </form>

              <div className="mt-4 pt-4 border-t border-slate-200 text-xs text-slate-500 text-center">
                🔒 Your data is safe and never shared with third parties.
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProjectDetail;
